/**
 * Right pane: Customer 360 context — linked customer card (click-through to
 * /customers/:id), one-click link suggestions, customer search, tagged order
 * plus recent orders, and the customer's other conversations (merged
 * per-human timeline across channels).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Link2,
  Loader2,
  MapPin,
  Package,
  Search,
  ShoppingBag,
  Unlink,
  User,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useCrmConversation,
  useCustomerContext,
  useCustomerSearch,
  useLinkCustomer,
  useLinkOrder,
  useUnlinkCustomer,
  useUnlinkOrder,
  type CrmConversation,
  type CrmCustomerSummary,
  type CrmLinkedOrder,
} from '@/api/bi/crmConversations';
import { notify } from '@/lib/toast';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ChannelIcon, channelMeta, STATUS_META } from './channelMeta';

const ORDER_STATUS_TONE: Record<string, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  completed: 'success',
  cancelled: 'danger',
  pending: 'warning',
};

export function ContextPane({
  conv: convSummary,
  onSelectConversation,
}: {
  conv: CrmConversation;
  onSelectConversation: (id: number) => void;
}) {
  // The list row has no messages; the detail query (already warm from the
  // thread pane) carries them for the shared-location section.
  const detail = useCrmConversation(convSummary.id);
  const conv = detail.data ?? convSummary;
  const ctx = useCustomerContext(conv.id);
  const linkCustomer = useLinkCustomer(conv.id);
  const unlinkCustomer = useUnlinkCustomer(conv.id);
  const linkOrder = useLinkOrder(conv.id);
  const unlinkOrder = useUnlinkOrder(conv.id);
  const [searchOpen, setSearchOpen] = useState(false);
  const [orderInput, setOrderInput] = useState('');

  const data = ctx.data;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto rounded-xl border border-border bg-surface p-3">
      {/* Customer card */}
      <section>
        <SectionTitle icon={<User className="h-3.5 w-3.5" />} title="Customer" />
        {ctx.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : data?.customer ? (
          <div className="rounded-lg border border-success/30 bg-success/5 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link
                  to={`/customers/${data.customer.user_id}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:text-primary hover:underline"
                >
                  <UserCheck className="h-4 w-4 text-success" />
                  {data.customer.name || `Customer #${data.customer.user_id}`}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </Link>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{data.customer.phone || 'No phone'}</p>
                {data.customer.email ? (
                  <p className="truncate text-[11px] text-muted-foreground">{data.customer.email}</p>
                ) : null}
              </div>
              <button
                type="button"
                title="Unlink customer"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-danger"
                onClick={async () => {
                  await unlinkCustomer.mutateAsync({});
                  notify.success('Customer unlinked');
                }}
              >
                <Unlink className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge tone="neutral">{data.customer.total_orders} orders</Badge>
              {data.customer.joined_at ? (
                <Badge tone="neutral">since {fmt.date(data.customer.joined_at, 'MMM yyyy')}</Badge>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border p-3">
            <p className="text-[11px] text-muted-foreground">
              Not linked to a Fasto account yet. The AI tags it automatically once the customer
              shares a verified mobile number.
            </p>
            {(data?.suggestions ?? []).map((s) => (
              <SuggestionRow
                key={s.user_id}
                suggestion={s}
                onLink={async () => {
                  await linkCustomer.mutateAsync({ user_id: s.user_id });
                  notify.success(`Linked to ${s.name || 'customer'}`);
                }}
              />
            ))}
            <Button
              size="sm"
              variant="outline"
              className="mt-2 w-full gap-1"
              onClick={() => setSearchOpen((v) => !v)}
            >
              <UserPlus className="h-3.5 w-3.5" /> Link manually
            </Button>
            {searchOpen ? (
              <CustomerSearchBox
                onPick={async (c) => {
                  await linkCustomer.mutateAsync({ user_id: c.user_id });
                  setSearchOpen(false);
                  notify.success(`Linked to ${c.name || 'customer'}`);
                }}
              />
            ) : null}
          </div>
        )}
      </section>

      {/* Shared location */}
      <SharedLocation conv={conv} />

      {/* Tagged order */}
      <section>
        <SectionTitle icon={<Package className="h-3.5 w-3.5" />} title="Tagged order" />
        {data?.tagged_order ? (
          <OrderCard
            order={data.tagged_order}
            highlight
            action={
              <button
                type="button"
                title="Untag order"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-danger"
                onClick={async () => {
                  await unlinkOrder.mutateAsync({});
                  notify.success('Order untagged');
                }}
              >
                <Unlink className="h-3.5 w-3.5" />
              </button>
            }
          />
        ) : (
          <div className="flex gap-1.5">
            <input
              className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary/50"
              placeholder="Order # to tag…"
              value={orderInput}
              inputMode="numeric"
              onChange={(e) => setOrderInput(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && orderInput) void tagOrder();
              }}
            />
            <Button size="sm" variant="outline" className="gap-1" disabled={!orderInput || linkOrder.isPending} onClick={() => void tagOrder()}>
              <Link2 className="h-3.5 w-3.5" /> Tag
            </Button>
          </div>
        )}
      </section>

      {/* Recent orders */}
      {data?.customer ? (
        <section>
          <SectionTitle icon={<ShoppingBag className="h-3.5 w-3.5" />} title="Recent orders" />
          {data.recent_orders.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No orders yet.</p>
          ) : (
            <div className="space-y-1.5">
              {data.recent_orders.slice(0, 6).map((o) => (
                <OrderCard
                  key={o.order_id}
                  order={o}
                  action={
                    data?.tagged_order?.order_id === o.order_id ? undefined : (
                      <button
                        type="button"
                        title="Tag this order to the conversation"
                        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-primary"
                        onClick={async () => {
                          await linkOrder.mutateAsync({ order_id: o.order_id });
                          notify.success(`Order #${o.order_id} tagged`);
                        }}
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                    )
                  }
                />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Other conversations (merged timeline) */}
      {data && data.other_conversations.length > 0 ? (
        <section>
          <SectionTitle icon={<Link2 className="h-3.5 w-3.5" />} title="Same customer elsewhere" />
          <div className="space-y-1">
            {data.other_conversations.map((oc) => {
              const status = STATUS_META[oc.status] ?? { label: oc.status, tone: 'neutral' as const };
              return (
                <button
                  key={oc.id}
                  type="button"
                  onClick={() => onSelectConversation(oc.id)}
                  className="flex w-full items-center gap-2 rounded-md border border-border/60 px-2 py-1.5 text-left hover:bg-muted/50"
                >
                  <ChannelIcon channel={oc.channel} />
                  <span className="min-w-0 flex-1 truncate text-[11px]">
                    {channelMeta(oc.channel).label}
                    {oc.subject ? ` · ${oc.subject}` : ''}
                  </span>
                  <Badge tone={status.tone} className="shrink-0">
                    {status.label}
                  </Badge>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Conversation meta */}
      <section className="mt-auto border-t border-border pt-2">
        <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
          <dt className="text-muted-foreground">Conversation</dt>
          <dd className="text-right">#{conv.id}</dd>
          <dt className="text-muted-foreground">Started</dt>
          <dd className="text-right">{conv.created_at ? fmt.date(conv.created_at) : '—'}</dd>
          <dt className="text-muted-foreground">AI</dt>
          <dd className="text-right">{conv.ai_paused ? 'Paused' : 'Active'}</dd>
          {conv.contact?.external_id ? (
            <>
              <dt className="text-muted-foreground">Channel ID</dt>
              <dd className="truncate text-right" title={conv.contact.external_id}>
                {conv.contact.external_id}
              </dd>
            </>
          ) : null}
        </dl>
      </section>
    </div>
  );

  async function tagOrder() {
    try {
      await linkOrder.mutateAsync({ order_id: Number(orderInput) });
      setOrderInput('');
      notify.success(`Order #${orderInput} tagged`);
    } catch {
      notify.error('Order not found');
    }
  }
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <h3 className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {icon}
      {title}
    </h3>
  );
}

function SuggestionRow({
  suggestion: s,
  onLink,
}: {
  suggestion: CrmCustomerSummary;
  onLink: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-primary/5 px-2 py-1.5 ring-1 ring-inset ring-primary/20">
      <div className="min-w-0">
        <p className="truncate text-xs font-medium">{s.name || `Customer #${s.user_id}`}</p>
        <p className="text-[10px] text-muted-foreground">
          {s.phone} · matched on {s.matched_on || 'phone'}
        </p>
      </div>
      <Button
        size="sm"
        className="shrink-0 gap-1"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onLink();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} Link
      </Button>
    </div>
  );
}

function CustomerSearchBox({ onPick }: { onPick: (c: CrmCustomerSummary) => Promise<void> }) {
  const [q, setQ] = useState('');
  const search = useCustomerSearch(q);
  const results = search.data?.results ?? [];
  return (
    <div className="mt-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          autoFocus
          className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:border-primary/50"
          placeholder="Search by phone, name, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {q.trim().length >= 3 ? (
        <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-border">
          {search.isLoading ? (
            <p className="p-2 text-[11px] text-muted-foreground">Searching…</p>
          ) : results.length === 0 ? (
            <p className="p-2 text-[11px] text-muted-foreground">No customers found.</p>
          ) : (
            results.map((c) => (
              <button
                key={c.user_id}
                type="button"
                className="flex w-full items-center justify-between gap-2 border-b border-border/50 px-2 py-1.5 text-left last:border-0 hover:bg-muted/60"
                onClick={() => void onPick(c)}
              >
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium">
                    {c.name || `Customer #${c.user_id}`}
                  </span>
                  <span className="block text-[10px] text-muted-foreground">
                    {c.phone} · {c.total_orders} orders
                  </span>
                </span>
                <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function OrderCard({
  order: o,
  action,
  highlight,
}: {
  order: CrmLinkedOrder;
  action?: React.ReactNode;
  highlight?: boolean;
}) {
  const tone = ORDER_STATUS_TONE[o.status.toLowerCase()] ?? 'info';
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border p-2',
        highlight ? 'border-primary/40 bg-primary/5' : 'border-border/70',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <Link
            to={`/orders/${o.order_id}`}
            className="text-xs font-semibold hover:text-primary hover:underline"
          >
            #{o.order_id}
          </Link>
          <Badge tone={tone}>{o.status || 'unknown'}</Badge>
        </div>
        <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
          {o.created_at ? fmt.datetime(o.created_at) : '—'}
          {o.store_name ? ` · ${o.store_name}` : ''}
          {o.total != null ? ` · ${fmt.npr(o.total)}` : ''}
        </p>
      </div>
      {action}
    </div>
  );
}

function SharedLocation({ conv }: { conv: CrmConversation }) {
  // The customer's shared GPS location arrives as a location media entry on a
  // message and is copied to the contact; surface the latest one if present.
  const last = [...(conv.messages ?? [])]
    .reverse()
    .flatMap((m) => (Array.isArray(m.media) ? m.media : []))
    .find((e) => e && e.kind === 'location' && e.latitude != null);
  if (!last) return null;
  return (
    <section>
      <SectionTitle icon={<MapPin className="h-3.5 w-3.5" />} title="Shared location" />
      <a
        href={`https://www.google.com/maps?q=${last.latitude},${last.longitude}`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-[11px] font-medium hover:bg-muted"
      >
        <MapPin className="h-3.5 w-3.5 text-primary" />
        {Number(last.latitude).toFixed(5)}, {Number(last.longitude).toFixed(5)} · open map
      </a>
    </section>
  );
}
